import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EditorView } from '@codemirror/view';
import { selectionRelease, useApp, useIsFocused, useSession, useUi, useWindow, useWindowId } from '../app/context';
import { notify, patchUi, setRendered } from '../app/ui';
import { createDocState } from '../editor/setup';
import { MarkdownIcon, OpenIcon, TextIcon, TrayIcon } from './icons';
import { shortcutHint } from './keys';
import { useLamp } from './pillParts';
import { BrandMark } from './Brand';

/**
 * The writing surface. One CodeMirror view lives here for the life of the
 * window; the controller swaps document states into it. When the window has
 * no document, the same live editor waits underneath the empty state: typing
 * — including an IME composition — goes straight into it and becomes a new
 * Markdown document without a single keystroke being re-routed.
 */
export function Surface() {
  const app = useApp();
  const windowId = useWindowId();
  const host = useRef<HTMLDivElement>(null);
  const empty = useWindow((w) => w.docIds.length === 0);
  const activeId = useWindow((w) => w.activeId);
  const md = useSession((s) => s.docs.find((d) => d.id === activeId)?.kind === 'md');
  const rendered = useUi((s) => md && !!activeId && s.rendered.has(activeId));

  useLayoutEffect(() => {
    if (!host.current) return;
    const view = new EditorView({ parent: host.current, state: createDocState('', 'md') });
    // Scroll edge: a hairline under the tabs appears only once text slides beneath them.
    const scrolled = () => host.current?.closest('.win-frame')?.toggleAttribute('data-scrolled', view.scrollDOM.scrollTop > 2);
    view.scrollDOM.addEventListener('scroll', scrolled, { passive: true });
    let pressed = false;
    const down = (e: MouseEvent) => {
      if (e.button !== 0) return;
      pressed = true;
      patchUi({ dragging: true, typing: false });
    };
    // Remember where the pointer let go: the selection pill appears there, not somewhere in the paragraph.
    const up = (e: MouseEvent) => {
      if (pressed) selectionRelease.set({ x: e.clientX, y: e.clientY, head: view.state.selection.main.head, view });
      pressed = false;
      patchUi({ dragging: false });
    };
    // A selection changed from the keyboard is anchored at the caret again.
    const key = () => selectionRelease.get()?.view === view && selectionRelease.set(null);
    view.contentDOM.addEventListener('keydown', key);
    view.contentDOM.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    // Right-click offers MyTXT's editing menu; with ⌥/Alt the browser's own (spelling, services) appears.
    const menu = (e: MouseEvent) => {
      if (e.altKey || e.shiftKey) return;
      e.preventDefault();
      patchUi({ contextMenu: { x: e.clientX, y: e.clientY, target: { kind: 'editor', windowId }, returnFocus: view.contentDOM } });
    };
    view.contentDOM.addEventListener('contextmenu', menu);
    app.attachView(windowId, view);
    return () => {
      app.attachView(windowId, null);
      view.scrollDOM.removeEventListener('scroll', scrolled);
      view.contentDOM.removeEventListener('mousedown', down);
      view.contentDOM.removeEventListener('keydown', key);
      view.contentDOM.removeEventListener('contextmenu', menu);
      window.removeEventListener('mouseup', up);
      view.destroy();
    };
  }, [app, windowId]);

  return (
    <div className="surface" data-scratch={empty || undefined} data-rendered={rendered || undefined}>
      {/* The editor stays alive under a rendered view: its text, selection and undo history are untouched. */}
      <div className="editor-host" ref={host} inert={rendered || undefined} aria-hidden={rendered || undefined} />
      {empty && <EmptyState />}
      {rendered && activeId && <RenderedView docId={activeId} />}
    </div>
  );
}

/**
 * A Markdown document read as it will look: rendered by a real Markdown
 * parser with raw HTML off, read-only, one step from the source again.
 */
function RenderedView({ docId }: { docId: string }) {
  const app = useApp();
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    let alive = true;
    import('../editor/render')
      .then(({ renderMarkdown }) => {
        if (!alive) return;
        try {
          setHtml(renderMarkdown(app.text(docId)));
        } catch (error) {
          console.error(error);
          setFailed(true);
        }
      })
      .catch((error) => {
        console.error(error);
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [app, docId]);

  // If it cannot be rendered, the source is shown again — with a word about why.
  useEffect(() => {
    if (!failed) return;
    setRendered(docId, false);
    notify('This document couldn’t be rendered. Showing its source.', 'error');
  }, [failed, docId]);

  const back = () => {
    setRendered(docId, false);
    app.focusEditor();
  };

  return (
    <article
      ref={ref}
      className="rendered"
      tabIndex={0}
      aria-label="Rendered Markdown (read-only)"
      data-ready={html !== null || undefined}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          back();
        }
      }}
      onDoubleClick={(e) => !(e.target as HTMLElement).closest('a') && back()}
    >
      {html !== null && (html.trim() ? <div className="rendered-body" dangerouslySetInnerHTML={{ __html: html }} /> : <p className="rendered-empty">Nothing to show yet. Switch to Source to write.</p>)}
    </article>
  );
}

const Hint = ({ spec }: { spec: string }) => {
  const label = shortcutHint(spec);
  return label ? <kbd>{label}</kbd> : null;
};

function EmptyState() {
  const app = useApp();
  const windowId = useWindowId();
  const focused = useIsFocused();
  const compact = useUi((s) => s.compact);

  // Pressing anywhere on the empty page puts the caret in the waiting editor.
  const focusEditor = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button, a')) return;
    e.preventDefault();
    app.focusWindow(windowId);
    app.focusEditor(windowId);
  };

  return (
    <EmptyStateView
      focused={focused}
      brand={compact}
      onPointerDown={focusEditor}
      onNew={(kind) => app.newDocument(kind, '', windowId)}
      onOpen={() => app.openFiles(windowId)}
    />
  );
}

interface EmptyViewProps {
  focused: boolean;
  /** On a compact screen the mark lives here, above the invitation. */
  brand: boolean;
  onPointerDown?: (e: React.PointerEvent) => void;
  onNew?: (kind: 'md' | 'txt') => void;
  onOpen?: () => void;
}

/**
 * The empty page: an invitation, three ways in, and a small marked place
 * for dropping files. (Also drawn, inert, by the replayed opening.)
 */
export function EmptyStateView({ focused, brand, onPointerDown, onNew, onOpen }: EmptyViewProps) {
  const tray = useRef<HTMLDivElement>(null);
  const lamp = useRef<HTMLDivElement>(null);
  useLamp(tray, lamp);
  return (
    <div className="empty" data-focused={focused || undefined} onPointerDown={onPointerDown}>
      {brand && (
        <div className="brand-slot">
          <BrandMark layout="stack" />
        </div>
      )}
      <p className="empty-line">
        <span className="empty-caret" aria-hidden="true" />
        <span className="empty-text">Start typing, or open a file.</span>
      </p>
      <div className="empty-actions" ref={tray} role="group" aria-label="Begin">
        <div className="pill-lamp" ref={lamp} aria-hidden="true" />
        <button type="button" className="empty-action" data-lamp onClick={() => onNew?.('md')}>
          <MarkdownIcon size={16} />
          <span>New Markdown</span>
          <Hint spec="Mod-n" />
        </button>
        <span className="empty-sep" aria-hidden="true" />
        <button type="button" className="empty-action" data-lamp onClick={() => onNew?.('txt')}>
          <TextIcon size={16} />
          <span>New Plain Text</span>
          <Hint spec="Mod-Alt-n" />
        </button>
        <span className="empty-sep" aria-hidden="true" />
        <button type="button" className="empty-action" data-lamp onClick={() => onOpen?.()}>
          <OpenIcon size={16} />
          <span>Open File</span>
          <Hint spec="Mod-o" />
        </button>
      </div>
      <DropZone />
    </div>
  );
}

/** The quiet, marked place for files: a small dashed outline that wakes when a file is over the window. */
function DropZone() {
  return (
    <div className="drop-zone" aria-hidden="true">
      <svg className="drop-zone-edge">
        <rect width="100%" height="100%" rx="10" ry="10" />
      </svg>
      <TrayIcon size={15} className="drop-zone-icon" />
      <span className="drop-zone-idle">Drop .md or .txt files</span>
      <span className="drop-zone-ready">Release to open here</span>
    </div>
  );
}
